// Browser notification service for native browser notifications
export class BrowserNotificationService {
  private static permission: NotificationPermission = 'default';

  // Initialize and request permission
  static async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support browser notifications');
      return false;
    }

    try {
      this.permission = await Notification.requestPermission();
      return this.permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  // Check if notifications are supported and permitted
  static isSupported(): boolean {
    if (!('Notification' in window)) {
      return false;
    }
    
    // Always check current permission status
    const currentPermission = this.getPermissionStatus();
    return currentPermission === 'granted';
  }

  // Get current permission status
  static getPermissionStatus(): NotificationPermission {
    if (!('Notification' in window)) {
      return 'denied';
    }
    this.permission = Notification.permission;
    return this.permission;
  }

  // Show browser notification
  static showNotification(
    title: string, 
    options: {
      body?: string;
      icon?: string;
      badge?: string;
      tag?: string;
      priority?: 'normal' | 'important' | 'urgent';
      data?: any;
      requireInteraction?: boolean;
    } = {}
  ): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.isSupported()) {
        console.warn('Browser notifications not supported or not permitted');
        resolve(false);
        return;
      }

      const notificationOptions: NotificationOptions = {
        body: options.body || '',
        icon: options.icon || '/images/KiraCar-logo-icon120.png',
        badge: '/images/KiraCar-logo-icon80.png',
        tag: options.tag || 'kiracar-notification',
        data: options.data,
        requireInteraction: options.priority === 'urgent' || options.priority === 'important',
        silent: false,
      };

      try {
        const notification = new Notification(title, notificationOptions);
        
        // Auto-close normal priority notifications after 8 seconds
        if (options.priority === 'normal') {
          setTimeout(() => {
            notification.close();
          }, 8000);
        } else if (options.priority === 'important') {
          // Important notifications stay for 15 seconds
          setTimeout(() => {
            notification.close();
          }, 15000);
        }
        // Urgent notifications require user interaction

        // Handle notification click
        notification.onclick = () => {
          window.focus();
          notification.close();
          
          // If we have data, we could navigate to a specific page
          if (options.data?.bookingId) {
            console.log('Navigate to booking:', options.data.bookingId);
          }
        };

        notification.onerror = (error) => {
          console.error('Notification error:', error);
        };

        resolve(true);
      } catch (error) {
        console.error('Error creating browser notification:', error);
        resolve(false);
      }
    });
  }

  // Show notification based on app notification type
  static showAppNotification(notification: {
    title: string;
    message: string;
    type: string;
    priority: 'normal' | 'important' | 'urgent';
    booking_id?: string;
    car_id?: string;
  }): Promise<boolean> {
    const icons = {
      booking: '/images/KiraCar-logo-icon120.png',
      maintenance: '/images/KiraCar-logo-icon120.png',
      insurance: '/images/KiraCar-logo-icon120.png',
      payment: '/images/KiraCar-logo-icon120.png',
      system: '/images/KiraCar-logo-icon120.png',
    };

    const tags = {
      booking: 'kiracar-booking',
      maintenance: 'kiracar-maintenance',
      insurance: 'kiracar-insurance',
      payment: 'kiracar-payment',
      system: 'kiracar-system',
    };

    return this.showNotification(notification.title, {
      body: notification.message,
      icon: icons[notification.type as keyof typeof icons] || icons.system,
      tag: tags[notification.type as keyof typeof tags] || tags.system,
      priority: notification.priority,
      data: {
        type: notification.type,
        bookingId: notification.booking_id,
        carId: notification.car_id,
      }
    });
  }

  // Initialize browser notifications with custom permission message
  static async initialize(permissionMessage?: string): Promise<boolean> {
    const permission = this.getPermissionStatus();
    
    if (permission === 'default') {
      // Show a friendly message before requesting permission
      const message = permissionMessage || 'KiraCar would like to send you notifications for important updates about your bookings and vehicles. Allow notifications?';
      const userWantsNotifications = confirm(message);
      
      if (userWantsNotifications) {
        return await this.requestPermission();
      }
      return false;
    }
    
    return permission === 'granted';
  }

  // Show a test notification with custom title and message
  static async showTestNotification(title?: string, message?: string): Promise<boolean> {
    const testTitle = title || 'KiraCar Test Notification';
    const testMessage = message || 'Browser notifications are working correctly!';
    
    return this.showNotification(testTitle, {
      body: testMessage,
      priority: 'normal',
      tag: 'kiracar-test'
    });
  }
} 